import { useState, useEffect } from 'react';
import * as api from '../services/api';
import dayjs from 'dayjs';

const THAI_BANKS = [
  'SCB','กสิกรไทย','กรุงเทพ','กรุงไทย','ทหารไทยธนชาต',
  'ออมสิน','ธ.ก.ส.','UOB','CIMB','LH Bank',
  'ทิสโก้','เกียรตินาคิน','อิออน','ซิตี้','อื่นๆ',
];

export default function CustomerInfoPanel({ conversation, pendingImport, onClose }) {
  const [info, setInfo]       = useState({ customer_name: '', customer_phone: '' });
  const [banks, setBanks]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [newBank, setNewBank] = useState({ bank_name: 'SCB', account_number: '' });
  const [addingBank, setAddingBank] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  const [media, setMedia]             = useState([]);
  const [lightbox, setLightbox]       = useState(null);
  const [syncing, setSyncing]         = useState(false);
  const [memberData, setMemberData]   = useState(null);
  const [pendingTx, setPendingTx]     = useState([]);
  const [syncError, setSyncError]     = useState('');

  useEffect(() => {
    if (!conversation?.id) return;
    api.getCustomerInfo(conversation.id)
      .then(data => {
        setInfo({ customer_name: data.customer_name || '', customer_phone: data.customer_phone || '' });
        setBanks(data.bank_accounts || []);
      })
      .catch(() => {});
    api.getConversationMedia(conversation.id)
      .then(setMedia)
      .catch(() => {});
  }, [conversation?.id]);

  useEffect(() => {
    if (!pendingImport) return;
    setInfo(prev => ({
      customer_name:  pendingImport.name  || prev.customer_name,
      customer_phone: pendingImport.phone || prev.customer_phone,
    }));
    if (pendingImport.bankAccounts?.length) {
      setBanks(prev => {
        const existing = new Set(prev.map(b => b.account_number));
        const newOnes  = pendingImport.bankAccounts.filter(b => !existing.has(b.account_number));
        return [...prev, ...newOnes.map(b => ({ ...b, id: 'pending_' + Math.random() }))];
      });
    }
  }, [pendingImport]);

  async function handleSave() {
    if (!conversation?.id) return;
    setSaving(true);
    try {
      const pendingBanks = banks.filter(b => String(b.id).startsWith('pending_'));
      // ส่ง null เมื่อ field ว่าง เพื่อให้ backend clear ค่าเดิมได้
      await api.importCustomerInfo(conversation.id, {
        customer_name:  info.customer_name.trim()  || null,
        customer_phone: info.customer_phone.trim() || null,
        bank_accounts:  pendingBanks.map(b => ({ bank_name: b.bank_name, account_number: b.account_number })),
      });
      const fresh = await api.getCustomerInfo(conversation.id);
      setInfo({ customer_name: fresh.customer_name || '', customer_phone: fresh.customer_phone || '' });
      setBanks(fresh.bank_accounts || []);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDeleteBank(bankId) {
    if (String(bankId).startsWith('pending_')) {
      setBanks(prev => prev.filter(b => b.id !== bankId));
      return;
    }
    try {
      await api.deleteBankAccount(conversation.id, bankId);
      setBanks(prev => prev.filter(b => b.id !== bankId));
    } catch (err) { alert(err.message); }
  }

  async function handleAddBank() {
    if (!newBank.account_number.trim()) return;
    setAddingBank(true);
    try {
      const created = await api.addBankAccount(conversation.id, newBank);
      setBanks(prev => [...prev, created]);
      setNewBank({ bank_name: 'SCB', account_number: '' });
      setShowAddBank(false);
    } catch (err) { alert(err.message); }
    finally { setAddingBank(false); }
  }

  // รีเซ็ต member data เมื่อเปลี่ยน conversation
  useEffect(() => { setMemberData(null); setPendingTx([]); setSyncError(''); }, [conversation?.id]);

  async function handleSync() {
    setSyncing(true); setSyncError(''); setMemberData(null); setPendingTx([]);
    try {
      const res = await api.syncMember(conversation.id);
      setMemberData(res.member);
      setPendingTx(res.pendingTx || []);
    } catch (err) {
      setSyncError(err.message);
    } finally { setSyncing(false); }
  }

  function handleSaveMember() {
    if (!memberData) return;
    const firstName = memberData.first_name || memberData.firstname || '';
    const lastName  = memberData.last_name  || memberData.lastname  || '';
    const fullName  = [firstName, lastName].filter(Boolean).join(' ');
    const phone     = memberData.mobile_phone || memberData.phone || memberData.mobile || memberData.username || '';
    const bankName  = memberData.bank_name  || memberData.bank  || '';
    const bankAcct  = memberData.bank_account || memberData.account_number || memberData.bankaccount || '';
    if (fullName) setInfo(p => ({ ...p, customer_name: fullName }));
    if (phone)    setInfo(p => ({ ...p, customer_phone: phone }));
    if (bankName && bankAcct) {
      const exists = banks.some(b => b.account_number === bankAcct);
      if (!exists) {
        setBanks(prev => [...prev, {
          id: 'pending_sync_' + Date.now(),
          bank_name: bankName,
          account_number: bankAcct,
        }]);
      }
    }
    setMemberData(null);
  }

  if (!conversation) return null;

  return (
    <aside className="customer-panel" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className="customer-panel-header">
        <div className="customer-panel-title">👤 ข้อมูลลูกค้า</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            className={`btn-sync${syncing ? ' syncing' : ''}`}
            onClick={handleSync}
            disabled={syncing || !info.customer_phone}
            title={info.customer_phone ? 'ดึงข้อมูลสมาชิกจากเบอร์โทร' : 'บันทึกเบอร์โทรก่อนจึงจะ Sync ได้'}
          >
            {syncing ? '⏳' : '🔄'} {syncing ? 'กำลังดึง...' : 'Sync'}
          </button>
          <button className="customer-panel-close" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Line display name (read-only) */}
      <div className="customer-line-info">
        <div className="customer-line-avatar">
          {conversation.picture_url
            ? <img src={conversation.picture_url} alt={conversation.display_name}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : (conversation.display_name || '?').slice(0, 2)
          }
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {conversation.display_name || 'ไม่ระบุชื่อ'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Line display name</div>
        </div>
      </div>

      {/* Customer name + phone */}
      <div className="customer-fields">
        <div className="customer-field">
          <label>ชื่อลูกค้า</label>
          <input
            value={info.customer_name}
            onChange={e => setInfo(p => ({ ...p, customer_name: e.target.value }))}
            placeholder="กรอกชื่อจริง..."
          />
        </div>
        <div className="customer-field">
          <label>เบอร์โทร</label>
          <input
            value={info.customer_phone}
            onChange={e => setInfo(p => ({ ...p, customer_phone: e.target.value }))}
            placeholder="0xx-xxx-xxxx"
          />
        </div>
      </div>

      {/* Sync error */}
      {syncError && (
        <div style={{ margin: '0 16px 10px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--red)' }}>
          ⚠️ {syncError}
        </div>
      )}

      {/* Member data from Sync */}
      {memberData && (
        <div className="sync-result-card">
          <div className="sync-result-header">
            <span className="sync-result-title">🏆 ข้อมูลสมาชิก</span>
            <span className={`sync-status-badge ${memberData.status === 'Active' ? 'active' : 'inactive'}`}>
              {memberData.status}
            </span>
          </div>
          <div className="sync-result-grid">
            {([memberData.firstname, memberData.lastname].filter(Boolean).join(' ')) && (
              <div className="sync-field">
                <span className="sync-label">ชื่อ-นามสกุล</span>
                <span className="sync-value">{[memberData.firstname, memberData.lastname].filter(Boolean).join(' ')}</span>
              </div>
            )}
            <div className="sync-field">
              <span className="sync-label">Username</span>
              <span className="sync-value">{memberData.username}</span>
            </div>
            {(memberData.balance ?? memberData.credit) != null && (
              <div className="sync-field">
                <span className="sync-label">ยอดเงิน</span>
                <span className="sync-value sync-credit">{Number(memberData.balance ?? memberData.credit).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</span>
              </div>
            )}
            {memberData.member_group?.name && (
              <div className="sync-field">
                <span className="sync-label">กลุ่มสมาชิก</span>
                <span className="sync-value">{memberData.member_group.name}</span>
              </div>
            )}
            {memberData.bank_account && (
              <div className="sync-field">
                <span className="sync-label">เลขบัญชี</span>
                <span className="sync-value">{memberData.bank_account}</span>
              </div>
            )}
            {memberData.agent_username && (
              <div className="sync-field">
                <span className="sync-label">Agent</span>
                <span className="sync-value">{memberData.agent_username}</span>
              </div>
            )}
          </div>

          {/* ฝาก/ถอนล่าสุด จาก sum_deposit_withdraw_obj */}
          {memberData.sum_deposit_withdraw_obj && (
            <div className="sync-tx-section">
              {memberData.sum_deposit_withdraw_obj.sum_deposit_cash_last > 0 && (
                <div className="sync-tx-row sync-tx-deposit">
                  <div className="sync-tx-left">
                    <span className="sync-tx-label">💰 ฝากล่าสุด</span>
                    <span className="sync-tx-date">{memberData.sum_deposit_withdraw_obj.sum_deposit_cash_last_date_time ? dayjs(memberData.sum_deposit_withdraw_obj.sum_deposit_cash_last_date_time).format('D MMM YY HH:mm') : ''}</span>
                  </div>
                  <span className="sync-tx-amount sync-tx-deposit-amt">+{Number(memberData.sum_deposit_withdraw_obj.sum_deposit_cash_last).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {memberData.sum_deposit_withdraw_obj.sum_withdraw_cash_last > 0 && (
                <div className="sync-tx-row sync-tx-withdraw">
                  <div className="sync-tx-left">
                    <span className="sync-tx-label">💸 ถอนล่าสุด</span>
                    <span className="sync-tx-date">{memberData.sum_deposit_withdraw_obj.sum_withdraw_cash_last_date_time ? dayjs(memberData.sum_deposit_withdraw_obj.sum_withdraw_cash_last_date_time).format('D MMM YY HH:mm') : ''}</span>
                  </div>
                  <span className="sync-tx-amount sync-tx-withdraw-amt">-{Number(memberData.sum_deposit_withdraw_obj.sum_withdraw_cash_last).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          )}

          {/* รอดำเนินการ */}
          {pendingTx.length > 0 && (
            <div className="sync-tx-section">
              <div className="sync-pending-header">⏳ รอดำเนินการ ({pendingTx.length})</div>
              {pendingTx.map(t => (
                <div key={t.objectId} className={`sync-tx-row ${t.type === 'Deposit' ? 'sync-tx-deposit' : 'sync-tx-withdraw'}`}>
                  <div className="sync-tx-left">
                    <span className="sync-tx-label">{t.type === 'Deposit' ? '💰 ฝาก' : '💸 ถอน'}</span>
                    <span className="sync-tx-date">{t.date_time ? dayjs(t.date_time).format('D MMM HH:mm') : ''}</span>
                  </div>
                  <span className={`sync-tx-amount ${t.type === 'Deposit' ? 'sync-tx-deposit-amt' : 'sync-tx-withdraw-amt'}`}>
                    {t.type === 'Deposit' ? '+' : '-'}{Number(t.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="sync-tx-status pending">{t.status}</span>
                </div>
              ))}
            </div>
          )}

          <button className="btn-save-member" onClick={handleSaveMember}>
            💾 บันทึกลงโปรไฟล์
          </button>
        </div>
      )}

      {/* ── Save button (prominent position) ─────── */}
      <div className="customer-save-row">
        <button
          className="btn-primary customer-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'กำลังบันทึก...' : saved ? '✓ บันทึกแล้ว' : '💾 บันทึกข้อมูล'}
        </button>
      </div>

      {/* Bank accounts */}
      <div className="customer-banks">
        <div className="customer-banks-header">
          <span className="customer-section-title">🏦 บัญชีธนาคาร</span>
          <button className="btn-add-bank" onClick={() => setShowAddBank(v => !v)}>
            {showAddBank ? '✕ ยกเลิก' : '+ เพิ่มบัญชี'}
          </button>
        </div>

        {banks.length === 0 && !showAddBank && (
          <div className="bank-empty">ยังไม่มีบัญชีธนาคาร</div>
        )}

        {banks.map(b => (
          <div key={b.id} className={`bank-row ${String(b.id).startsWith('pending_') ? 'bank-row-pending' : ''}`}>
            <div className="bank-row-body">
              {/* แยกธนาคาร และเลขบัญชีชัดเจน */}
              <div className="bank-field">
                <span className="bank-field-label">ธนาคาร</span>
                <span className="bank-field-value bank-name-value">{b.bank_name}</span>
              </div>
              <div className="bank-field">
                <span className="bank-field-label">เลขบัญชี</span>
                <span className="bank-field-value bank-account-value">{b.account_number}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              {String(b.id).startsWith('pending_') && (
                <span className="bank-pending-badge">รอบันทึก</span>
              )}
              <button className="bank-delete" onClick={() => handleDeleteBank(b.id)} title="ลบ">🗑</button>
            </div>
          </div>
        ))}

        {showAddBank && (
          <div className="add-bank-form">
            <div className="add-bank-field">
              <label className="add-bank-label">ธนาคาร</label>
              <select
                value={newBank.bank_name}
                onChange={e => setNewBank(p => ({ ...p, bank_name: e.target.value }))}
                className="bank-select"
              >
                {THAI_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="add-bank-field">
              <label className="add-bank-label">เลขบัญชี</label>
              <input
                className="bank-account-input"
                value={newBank.account_number}
                onChange={e => setNewBank(p => ({ ...p, account_number: e.target.value }))}
                placeholder="เช่น 1234567890"
                maxLength={20}
                onKeyDown={e => e.key === 'Enter' && handleAddBank()}
              />
            </div>
            <button
              className="btn-primary btn-add-confirm"
              onClick={handleAddBank}
              disabled={addingBank || !newBank.account_number.trim()}
            >
              {addingBank ? '...' : '+ เพิ่มบัญชีนี้'}
            </button>
          </div>
        )}
      </div>
      {/* Media gallery */}
      {media.length > 0 && (
        <div className="customer-media">
          <div className="customer-banks-header">
            <span className="customer-section-title">🖼 สลิป / รูปภาพ ({media.length})</span>
          </div>
          <div className="media-grid">
            {media.map(m => (
              <div key={m.id} className="media-thumb" onClick={() => setLightbox(m)} title={dayjs(m.created_at).format('D MMM HH:mm')}>
                <img src={m.media_url} alt="สลิป" loading="lazy" />
                <div className="media-date">{dayjs(m.created_at).format('D/M HH:mm')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="media-lightbox" onClick={() => setLightbox(null)}>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            <img src={lightbox.media_url} alt="สลิป" className="lightbox-img" />
            <div className="lightbox-meta">
              {dayjs(lightbox.created_at).format('D MMMM YYYY HH:mm น.')}
            </div>
            <a href={lightbox.media_url} target="_blank" rel="noreferrer" className="lightbox-download">
              ⬇ ดาวน์โหลด
            </a>
          </div>
        </div>
      )}
    </aside>
  );
}
